/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { getProperties, action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import MediaTypes from '@linc-technologies/linced-common/enums/media-type';
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import config from 'linced-admin/config/environment';
import { getKeyAndSourceFromUpload } from '@linc-technologies/linced-common/utils/uppy';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { setDiff } from '@ember/object/computed';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class OauthClient extends Component {
  // Attrs
  clientProperties = Object.freeze([
    'id',
    'allowedAudiences',
    'allowedRegions',
    'allowedTenantAccess',
    'grantTypes',
    'responseTypes',
    'scopes',
    'public',
    'disabled',
    'name',
    'secret',
    'redirectUris',
    'owner',
    'policyUri',
    'termsOfServiceUri',
    'clientUri',
    'logoUri',
    'contacts',
    'published',
  ]);

  heroAllowedAudiences = Object.freeze([
    {
      description: 'Development APIs',
      value: 'https://dev-api.linc-ed.com',
    },
    {
      description: 'Staging APIs',
      value: 'https://staging-api.linc-ed.com',
    },
    {
      description: 'Production APIs',
      value: 'https://api4.linc-ed.com',
    },
    {
      description: 'Gatekeeper',
      value: 'https://go.linc-ed.com',
    },
  ]);

  heroAllowedGrantTypes = Object.freeze([
    {
      description: 'Authorization Code',
      value: 'authorize_code',
    },
    {
      description: 'Client Credentials',
      value: 'client_credentials',
    },
    {
      description: 'Implicit',
      value: 'implicit',
    },
  ]);

  heroAllowedResponseTypes = Object.freeze([
    {
      description: 'Authorization Code',
      value: 'code',
    },
    {
      description: 'Access Token',
      value: 'token',
    },
  ]);

  // Services
  @service
  media;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Props
  // client provides the OAuth 2.0 client to be editing.
  client = null;

  // heroAllowedScopes provides all the allowed region scopes that can be
  // assigned to an OAuth Client.
  heroAllowedRegions = null;

  // heroAllowedScopes provides all the scopes that can be assigned to an OAuth
  // Client.
  heroAllowedScopes = null;

  // heroTenants provides all the schools we currently have on file, to enable
  // a client access.
  heroTenants = null;

  // newClient provides a boolean to enable creating a brand-new OAuth client.
  newClient = false;

  // onCreate provides the callback function to return the newly created
  // client to show the returned client ID and Secret.
  // @param {DS.Model<module:linced-admin/models/client} - an oauth2 client.
  onCreate() {}

  // State
  // clientEditOpen provides the state of the OAuth client edit dialog.
  clientEditOpen = false;

  // clientDeleteOpen provides the state of the OAuth client delete dialog.
  @tracked
  clientDeleteOpen = false;

  // remainingAllowedAudiences calculates the difference between the currently
  // selected audiences and the allowed Audiences to return the remaining items
  // that can be added to the client.
  @setDiff('heroAllowedAudiences', 'selectedAudiences')
  remainingAllowedAudiences;

  // remainingAllowedRegions calculates the difference between the currently
  // selected regions and the allowed Regions to return the remaining items
  // that can be added to the client.
  @setDiff('heroAllowedRegions', 'selectedRegions')
  remainingAllowedRegions;

  // remainingAllowedScopes calculates the difference between the currently
  // selected scopes and the allowed Scopes to return the remaining items that
  // can be added to the client.
  @setDiff('heroAllowedScopes', 'selectedScopes')
  remainingAllowedScopes;

  // remainingAllowedTenants calculates the difference between the currently
  // selected tenants and the allowed Tenants to return the remaining items
  // that can be added to the client.
  @setDiff('heroTenants', 'selectedTenants')
  remainingAllowedTenants;

  // remainingAllowedGrantTypes calculates the difference between the currently
  // selected granttypes and the allowed GrantTypes to return the remaining
  // items that can be added to the client.
  @setDiff('heroAllowedGrantTypes', 'selectedGrantTypes')
  remainingAllowedGrantTypes;

  // remainingAllowedResponseTypes calculates the difference between the
  // currently selected responsetypes and the allowed ResponseTypes to return
  // the remaining items that can be added to the client.
  @setDiff('heroAllowedResponseTypes', 'selectedResponseTypes')
  remainingAllowedResponseTypes;

  // saveInProgress provides the current state of when a record save is in
  // progress.
  @tracked
  saveInProgress = false;

  // uploading provides the current state of uploading the OAuth Client's logo.
  @tracked
  uploading = false;

  /* Client properties State */
  // id contains the provided client's id field, to enable operations to happen
  // on the copied attribute, rather than the datastore object itself in order
  // to prevent data munging.
  @tracked
  id = '';

  // allowedAudiences contains the provided client's allowedAudiences field, to
  // enable operations to happen on the copied attribute, rather than the
  // datastore object itself in order to prevent data munging.
  @tracked
  allowedAudiences = null;

  // allowedRegions contains the provided client's allowedRegions field, to
  // enable operations to happen on the copied attribute, rather than the
  // datastore object itself in order to prevent data munging.
  @tracked
  allowedRegions = null;

  // allowedTenantAccess contains the provided client's allowedTenantAccess
  // field, to enable operations to happen on the copied attribute, rather than
  // the datastore object itself in order to prevent data munging.
  @tracked
  allowedTenantAccess = null;

  // grantTypes contains the provided client's grantTypes field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  grantTypes = null;

  // responseTypes contains the provided client's responseTypes field, to
  // enable operations to happen on the copied attribute, rather than the
  // datastore object itself in order to prevent data munging.
  @tracked
  responseTypes = null;

  // scopes contains the provided client's scopes field, to enable operations
  // to happen on the copied attribute, rather than the datastore object itself
  // in order to prevent data munging.
  @tracked
  scopes = null;

  // public contains the provided client's public field, to enable operations
  // to happen on the copied attribute, rather than the datastore object itself
  // in order to prevent data munging.
  @tracked
  public = false;

  @action
  setPublic(value) {
    this.public = value;
  }

  // disabled contains the provided client's disabled field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  disabled = false;

  @action
  setDisabled(value) {
    this.disabled = value;
  }

  // name contains the provided client's name field, to enable operations to
  // happen on the copied attribute, rather than the datastore object itself in
  // order to prevent data munging.
  @tracked
  name = '';

  @action
  setName(value) {
    this.name = value;
  }

  // secret contains the provided client's secret field, to enable operations
  // to happen on the copied attribute, rather than the datastore object itself
  // in order to prevent data munging.
  @tracked
  secret = '';

  // redirectUris contains the provided client's redirectUris field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  redirectUris = null;

  // owner contains the provided client's owner field, to enable operations to
  // happen on the copied attribute, rather than the datastore object itself in
  // order to prevent data munging.
  @tracked
  owner = '';

  @action
  setOwner(value) {
    this.owner = value;
  }

  // policyUri contains the provided client's policyUri field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  policyUri = '';

  @action
  setPolicyUri(value) {
    this.policyUri = value;
  }

  // termsOfServiceUri contains the provided client's termsOfServiceUri field,
  // to enable operations to happen on the copied attribute, rather than the
  // datastore object itself in order to prevent data munging.
  @tracked
  termsOfServiceUri = '';

  @action
  setTermsOfServiceUri(value) {
    this.termsOfServiceUri = value;
  }

  // clientUri contains the provided client's clientUri field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  clientUri = '';

  @action
  setClientUri(value) {
    this.clientUri = value;
  }

  // logoUri contains the provided client's logoUri field, to enable operations
  // to happen on the copied attribute, rather than the datastore object itself
  // in order to prevent data munging.
  @tracked
  logoUri = '';

  // contacts contains the provided client's contacts field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  contacts = null;

  // published contains the provided client's published field, to enable
  // operations to happen on the copied attribute, rather than the datastore
  // object itself in order to prevent data munging.
  @tracked
  published = false;

  @action
  setPublished(value) {
    this.published = value;
  }

  @tracked
  deleteInProgress;

  // Methods
  /**
   * selectedAudiences provides a computed property to return the mapped
   * audience objects that are currently in use by the OAuth client to enable
   * the pretty chips component to render.
   */
  @computed('allowedAudiences.[]', 'heroAllowedAudiences.[]')
  get selectedAudiences() {
    if (isEmpty(this.allowedAudiences)) {
      return [];
    }
    return this.allowedAudiences.map((aud) => {
      return findBy(this.heroAllowedAudiences, 'value', aud);
    });
  }

  // selectedGrantTypes provides a computed property to return the mapped oauth
  // grant types that are currently in use by the OAuth client to enable the
  // pretty chips component to render.
  @computed('grantTypes.[]', 'heroAllowedGrantTypes.[]')
  get selectedGrantTypes() {
    if (isEmpty(this.grantTypes)) {
      return [];
    }
    return this.grantTypes.map((grantType) => {
      return findBy(this.heroAllowedGrantTypes, 'value', grantType);
    });
  }

  /**
   * selectedResponseTypes provides a computed property to return the mapped
   * oauth response types that are currently in use by the OAuth client to
   * enable the pretty chips component to render.
   */
  @computed('responseTypes.[]', 'heroAllowedResponseTypes.[]')
  get selectedResponseTypes() {
    if (isEmpty(this.responseTypes)) {
      return [];
    }
    return this.responseTypes.map((responseType) => {
      return findBy(this.heroAllowedResponseTypes, 'value', responseType);
    });
  }

  /**
   * selectedRegions provides a computed property to return the mapped regions
   * that are currently in use by the OAuth client to enable the pretty chips
   * component to render.
   */
  @computed('allowedRegions.[]', 'heroAllowedRegions.[]')
  get selectedRegions() {
    if (isEmpty(this.allowedRegions)) {
      return [];
    }
    return this.allowedRegions.map((region) => {
      return findBy(this.heroAllowedRegions, 'enum', region);
    });
  }

  /**
   * selectedScopes provides a computed property to return the mapped scopes
   * that are currently in use by the OAuth client to enable the pretty chips
   * component to render.
   */
  @computed('scopes.[]', 'heroAllowedScopes.[]')
  get selectedScopes() {
    if (isEmpty(this.scopes)) {
      return [];
    }
    return this.scopes.map((scope) => {
      return findBy(this.heroAllowedScopes, 'value', scope);
    });
  }

  /**
   * selectedTenants provides a computed property to return the mapped schools
   * that are currently in use by the OAuth client to enable the pretty contact
   * chips component to render.
   */
  @computed('allowedTenantAccess.[]', 'heroTenants.[]')
  get selectedTenants() {
    if (isEmpty(this.allowedTenantAccess)) {
      return [];
    }
    return this.allowedTenantAccess.map((tid) => {
      return findBy(this.heroTenants, 'id', tid);
    });
  }

  /**
   * isRegionScope checks to see if a provided scope is a jurisdiction based
   * scope to make it easy to determine if the entered scope's enum also needs
   * to be added to allowed regions.
   *
   * @param {string} scope
   * @returns {boolean}
   */
  isRegionScope(scope) {
    return scope.value.startsWith('urn:hero:jurisdictions');
  }

  /**
   * resetForm resets the required state properties to defaults.
   */
  resetForm() {
    this.id = '';
    this.allowedAudiences = [];
    this.allowedRegions = [];
    this.allowedTenantAccess = [];
    this.grantTypes = [];
    this.responseTypes = [];
    this.scopes = [];
    this.public = false;
    this.disabled = false;
    this.name = '';
    this.secret = '';
    this.redirectUris = [];
    this.owner = '';
    this.policyUri = '';
    this.termsOfServiceUri = '';
    this.clientUri = '';
    this.logoUri = '';
    this.contacts = [];
    this.published = false;

    // State reset
    this.uploading = false;
  }

  /**
   * toggleEdit toggles the edit client dialog open, resets the form and
   * creates a deep copy of the provided client into the components client
   * state variables, ready for editing.
   */
  toggleEdit() {
    let client = this.client;

    this.toggleProperty('clientEditOpen');
    if (!this.clientEditOpen) {
      this.resetForm();
      return;
    }

    if (!isEmpty(client)) {
      this.setProperties(client.getProperties(this.clientProperties));

      // deep copy arrays
      this.allowedAudiences = client.allowedAudiences.slice();
      this.allowedRegions = client.allowedRegions.slice();
      this.allowedTenantAccess = client.allowedTenantAccess.slice();
      this.grantTypes = client.grantTypes.slice();
      this.responseTypes = client.responseTypes.slice();
    }
  }

  /**
   * createClient creates a new OAuth client on the server.
   *
   * @param {Object} props - contains the client properties that are wanting to
   *     be pushed into the client on create.
   */
  createClient(props) {
    let newClient = this.store.createRecord(
      'client',
      getProperties(this, props),
    );

    newClient
      .save()
      .then((client) => {
        this.onCreate(client);
        this.toaster.success('OAuth Client created successfully!');
        this.toggleEdit();
      })
      .catch((err) => {
        this.toaster.error(`Error creating new OAuth Client! ${err}`);
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  /**
   * updateClient updates the current OAuth client on the server.
   *
   * @param {Object} props - contains the client properties that are wanting to
   *     be pushed into the client on update.
   */
  updateClient(props) {
    this.store
      .findRecord('client', this.id)
      .then((client) => {
        client.setProperties(getProperties(this, props));

        return client.save();
      })
      .then(() => {
        this.toaster.success('OAuth Client saved successfully!');
        this.toggleEdit();
      })
      .catch((err) => {
        this.toaster.error(`Error saving OAuth Client! ${err}`);
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  /**
   * deleteClient deletes the current OAuth client from the server.
   */
  deleteClient() {
    this.deleteInProgress = true;
    if (!isEmpty(this.onDelete)) {
      this.onDelete(this.client);

      this.deleteInProgress = false;
      this.clientDeleteOpen = false;
      return;
    }

    this.store
      .findRecord('client', this.client.id, { backgroundReload: false })
      .then(function (client) {
        client.destroyRecord();
      })
      .then(() => {
        this.toaster.success('Successfully deleted OAuth Client!');
      })
      .catch((err) => {
        this.toaster.error(`Error deleting OAuth Client! ${err}`);
      })
      .finally(() => {
        // A successful destroyRecord() removes the client from the store,
        // which can tear down this component (e.g. via a list re-render)
        // before this callback fires -- guard against setting properties
        // on an already-destroyed component.
        if (this.isDestroying || this.isDestroyed) {
          return;
        }
        this.deleteInProgress = false;
        this.clientDeleteOpen = false;
      });
  }

  @action
  noOp() {}

  // addAudience adds a audience to the locally copied client's audiences.
  @action
  addAudience(audience) {
    this.allowedAudiences = [...this.allowedAudiences, audience.value];
  }

  // removeAudience removes a audience from the locally copied client's
  // audiences.
  @action
  removeAudience(audience) {
    this.allowedAudiences = this.allowedAudiences.filter(
      (item) => item !== audience.value,
    );
  }

  // addContact adds a contact to the locally copied client's contacts.
  @action
  addContact(contact) {
    this.contacts = [...this.contacts, contact];
  }

  // removeContact removes a contact from the locally copied client's
  // contacts.
  @action
  removeContact(contact) {
    this.contacts = this.contacts.filter((item) => item !== contact);
  }

  // addGrantType adds a grant type to the locally copied client's grant
  // types.
  @action
  addGrantType(grantType) {
    this.grantTypes = [...this.grantTypes, grantType.value];
  }

  // removeGrantType removes a grant type from the locally copied client's
  // grant types.
  @action
  removeGrantType(grantType) {
    this.grantTypes = this.grantTypes.filter(
      (item) => item !== grantType.value,
    );
  }

  // addRedirectUri adds a redirect uri to the locally copied client's
  // redirect uris.
  @action
  addRedirectUri(redirectUri) {
    this.redirectUris = [...this.redirectUris, redirectUri];
  }

  // removeRedirectUri removes a redirect uri from the locally copied client's
  // redirect uris.
  @action
  removeRedirectUri(redirectUri) {
    this.redirectUris = this.redirectUris.filter(
      (item) => item !== redirectUri,
    );
  }

  // addResponseType adds a response type to the locally copied client's
  // response types.
  @action
  addResponseType(responseType) {
    this.responseTypes = [...this.responseTypes, responseType.value];
  }

  // removeResponseType removes a response type from the locally copied
  // client's response types.
  @action
  removeResponseType(responseType) {
    this.responseTypes = this.responseTypes.filter(
      (item) => item !== responseType.value,
    );
  }

  // addScope adds a scope to the locally copied client's scopes.
  @action
  addScope(scope) {
    if (this.isRegionScope(scope)) {
      this.allowedRegions = [...this.allowedRegions, scope.enum];
    }

    this.scopes = [...this.scopes, scope.value];
  }

  // removeScope removes a scope from the locally copied client's scopes.
  @action
  removeScope(scope) {
    if (this.isRegionScope(scope)) {
      this.allowedRegions = this.allowedRegions.filter(
        (item) => item !== scope.enum,
      );
    }
    this.scopes = this.scopes.filter((item) => item !== scope.value);
  }

  // addTenant adds a tenant to the locally copied client's tenants.
  @action
  addTenant(tenant) {
    this.allowedTenantAccess = [...this.allowedTenantAccess, tenant.id];
  }

  // removeTenant removes a tenant from the locally copied client's tenants.
  @action
  removeTenant(tenant) {
    this.allowedTenantAccess = this.allowedTenantAccess.filter(
      (item) => item !== tenant.id,
    );
  }

  // toggleClientEdit toggles the client edit dialog on or off.
  @action
  toggleClientEdit() {
    this.toggleEdit();
  }

  // toggleClientDelete toggles the client delete dialog on or off.
  @action
  toggleClientDelete() {
    this.toggleProperty('clientDeleteOpen');
    if (!this.clientDeleteOpen) {
      this.resetForm();
    }
  }

  // onSave performs either a save, or update based on whether the provided
  // newClient property is set to true or false.
  @action
  onSave() {
    let properties = this.clientProperties;
    this.saveInProgress = true;

    if (this.newClient) {
      this.createClient(properties);
    } else {
      this.updateClient(properties);
    }
  }

  // onDelete performs a client deletion.
  @action
  onDelete() {
    this.deleteClient();
  }

  /**
   * onUploadStart provides the callback for when a profile picture has
   * started being uploaded.
   */
  @action
  onUploadStart() {
    this.uploading = true;
  }

  /**
   * onUpload provides the callback for handling what to do after a profile
   * picture has been uploaded.
   *
   * @param file
   * @param upload
   */
  @action
  onUpload(file, upload) {
    let { key, mediaSource } = getKeyAndSourceFromUpload(upload);
    let currentUri = this.logoUri;
    this.logoUri = '';

    this.store
      .createRecord('media', {
        schoolId: this.get('session.school'),
        personId: this.get('session.person'),
        sharedWith: SharingLevel.PUBLIC,
        mediaType: MediaTypes.IMAGE,
        mediaSource: mediaSource,
        mimeType: file.type,
        fileName: file.name,
        storageKey: key,
        fileSize: file.size,
      })
      .save()
      .then((media) => {
        // TODO: We (HERO STAFF) are the only ones that care about this.
        //  We do not store an id on the client model so we can't do the
        //  blobby serving. This will work until we nuke the unauthenticated media routes.
        this.logoUri = `${config.APP.apiHost}/media/${config.APP.apiPath}/media/${
          media.id
        }/${encodeURIComponent(media.fileName)}`;
        this.toaster.success('Uploaded OAuth Client Logo!');
      })
      .catch(() => {
        // Revert unsaved changes, i.e. the profileMediaId
        this.logoUri = currentUri;
        this.toaster.error('Error saving OAuth Client Logo!');
      })
      .finally(() => {
        this.uploading = false;
      });
  }

  /**
   * onUploadError provides the callback to deal with upload errors.
   */
  @action
  onUploadError() {
    this.toaster.error('Error saving OAuth Client Logo!');
  }

  // Life Cycle hooks
  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (isEmpty(this.client)) {
      // If no client is presented, set everything to expected default values.
      this.resetForm();
    }
  }
}
